<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import Icon from "@/design/primitives/Icon.vue";
import RichText from "@/shared/rich-text/RichText.vue";
import { fmtElapsed, readableJson } from "../composables/activity";
import type { LiveTool } from "../composables/use-turn-stream";
import { useAgentsI18n } from "../i18n/use-agents-i18n";
import AgentActivity from "./AgentActivity.vue";

/**
 * O turno que está a acontecer, num cartão só.
 *
 * O que ele MOSTRA é o mesmo cartão de atividade que fica na fala depois — ver
 * `AgentActivity.vue`. Estava aqui uma segunda lista de ferramentas com regras
 * próprias, e a consequência era visível a olho: a pessoa via o Ben trabalhar
 * numa lista e, quando o turno acabava, a lista mudava de forma debaixo dela.
 *
 * O que fica aqui é o que só existe ENQUANTO corre: o texto parcial com o cursor,
 * e a linha de espera com o cronómetro.
 *
 * ── O CRONÓMETRO ──────────────────────────────────────────────────────────
 *
 * `pensando · 12s`, como na V2. Sem ele, quinze segundos de silêncio e um turno
 * pendurado desenham-se exactamente da mesma maneira — e a pessoa fica a olhar
 * para um ponto a pulsar sem saber se espera ou se recarrega.
 */
const props = defineProps<{
	tools: readonly LiveTool[];
	text: string;
	thinking: string;
	/** A peça grande sendo escrita agora — null fora de uma escrita. Ver o porquê
	 *  em `use-turn-stream.ts`: minutos de escrita eram silêncio absoluto. */
	writing?: { tool: string; chars: number; text?: string } | null;
}>();

const { t } = useAgentsI18n();

const startedAt = Date.now();
const now = ref(startedAt);
let tick: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
	tick = setInterval(() => {
		now.value = Date.now();
	}, 1000);
});

onBeforeUnmount(() => {
	if (tick) clearInterval(tick);
});

const elapsed = computed(() => fmtElapsed(now.value - startedAt));
/** Uma ferramenta a correr ganha ao texto: é o facto mais interessante e o único
 *  sem limite de tempo. A mesma regra que decide o estado do orbe. */
const acting = computed(() => props.tools.some((tool) => tool.output === undefined));
/** O tamanho em KB, arredondado — o número que cresce é o que prova vida. */
const writingKb = computed(() =>
	props.writing ? Math.max(1, Math.round(props.writing.chars / 1024)) : 0,
);
const writingText = computed(() => readableJson(props.writing?.text ?? ""));
</script>

<template>
	<div
		class="max-w-[92%] space-y-2 py-2"
	>
		<AgentActivity live part="steps" :tools="tools" :thinking="thinking" />

		<!-- Rico ENQUANTO transmite, não só no último quadro: as respostas do Ben são
		     tabelas, listas e código, e como texto puro elas chegam como um muro e
		     se ajeitam quando o fluxo acaba — o que se lê como a página se redesenhando. -->
		<div v-if="text" class="font-serif text-[1.0625rem] leading-7 text-fg">
			<RichText :text="text" origin="agent" />
			<span class="animate-pulse" aria-hidden="true">▍</span>
		</div>

		<!-- A ESCRITA LONGA, dita enquanto acontece: ferramenta, tamanho crescendo e
		     cronómetro. Oito minutos disto sem uma linha eram indistinguíveis de um
		     travamento — a pessoa recarregava um turno saudável. Fica visível mesmo
		     com texto parcial acima: o texto parou, a escrita é o que está vivo. -->
		<p v-if="writing" class="flex items-center gap-1.5 text-xs text-fg-subtle">
			<Icon name="pencil" class="size-3 animate-pulse" />
			<span>{{ t("live.writing", { tool: writing.tool, kb: writingKb }) }}</span>
			<span class="tabular-nums">· {{ elapsed }}</span>
		</p>

		<!-- Nem cartão nem texto: a única coisa a dizer é que ele está nisto, e há
		     quanto tempo. -->
		<p v-else-if="!text" class="flex items-center gap-1.5 text-xs text-fg-subtle">
			<span class="animate-pulse" aria-hidden="true">◌</span>
			<span>{{ acting ? t("live.acting") : t("live.thinking") }}</span>
			<span class="tabular-nums">· {{ elapsed }}</span>
		</p>

		<!-- O CÓDIGO A CORRER: a janela viva do que o modelo escreve. `flex-col-reverse`
		     ancora a rolagem no FIM nativamente — o mais recente sempre visível, sem
		     JS a perseguir o scroll a cada quadro. Nada anima além do texto crescer. -->
		<div
			v-if="writing && writingText"
			class="relative rounded border border-border bg-bg-muted"
			data-testid="live-writing"
		>
			<span
				class="absolute top-1 right-2 text-2xs uppercase tracking-wide text-fg-subtle"
				>{{ t("live.writingLive") }}</span
			>
			<div class="flex max-h-40 flex-col-reverse overflow-y-auto">
				<pre
					class="whitespace-pre-wrap break-all px-2 py-1.5 font-mono text-2xs leading-snug text-fg-muted"
				>{{ writingText }}</pre>
			</div>
		</div>
	</div>
</template>
